<script setup lang="ts">
import {ref, computed, watch, nextTick} from 'vue';
import {useI18n} from 'vue-i18n';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import LoaderAnimation from '@/vue/molecules/loader-animation.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import type {SetupIntentResponse} from '@/ts/types/dto/user-dto';

 
type StripeInstance = any;
 
type StripeCardElement = any;

interface StripeElements {
  stripe: StripeInstance;
  cardElement: StripeCardElement;
}

declare global {
  interface Window {

    Stripe?(key: string, options?: {locale?: string}): StripeInstance;
  }
}

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  updateSuccess: [];
  updateError: [error: string];
}>();

enum UpdateState {
  LOADING = 'loading',
  FORM = 'form',
  SUCCESS = 'success',
  ERROR = 'error',
}

const api = useApi();
const logger = useLogger('update-card-dialog');
const {t, locale} = useI18n();
const updateState = ref<UpdateState>(UpdateState.LOADING);
const setupIntentResponse = ref<SetupIntentResponse | null>(null);
const error = ref<string | null>(null);
const stripeLoaded = ref(false);
const stripeCardElement = ref<StripeElements | null>(null);
const isProcessing = ref(false);

const dialogValue = computed({
  get: () => props.modelValue,
  set: (value: boolean) => { emit('update:modelValue', value); },
});

const closeDialog = (): void => {
  if (stripeCardElement.value?.cardElement) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      stripeCardElement.value.cardElement.unmount();
    } catch {
      // Element may already be unmounted
    }
  }

  dialogValue.value = false;
  setupIntentResponse.value = null;
  stripeCardElement.value = null;
  error.value = null;
  updateState.value = UpdateState.LOADING;
  stripeLoaded.value = false;
};

const getCssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const setupStripeElements = (): void => {
  if (!window.Stripe || !setupIntentResponse.value) {
    return;
  }

  updateState.value = UpdateState.FORM;

  void nextTick(() => {
    const cardElementContainer = document.querySelector('#update-card-element');
    if (!cardElementContainer) {
      setTimeout(setupStripeElements, 100);
      return;
    }

    // Stripe defaults to the browser language, which ignores the language the profile is set to.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const stripe: StripeInstance = window.Stripe?.(
      setupIntentResponse.value?.publicKey ?? '',
      {locale: locale.value},
    );

    const colorPrimary = getCssVar('--Primary-primary-color-1');
    const colorBackground = getCssVar('--Neutral-UI-neutral-ui-10');
    const colorText = getCssVar('--Neutral-UI-neutral-ui-100');
    const colorDanger = getCssVar('--Primary-primary-color-3');
    const colorPlaceholder = getCssVar('--Neutral-UI-neutral-ui-50');
    const fontFamily = getCssVar('--Font-Primary-Font');

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
       @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const elements = stripe.elements({
      appearance: {
        theme: 'flat',
        variables: {
          colorPrimary,
          colorBackground,
          colorText,
          colorDanger,
          fontFamily,
          spacingUnit: '8px',
          borderRadius: '8px',
        },
      },
    });

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
       @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const cardElement: StripeCardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: colorText,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '::placeholder': {
            color: colorPlaceholder,
          },
        },
      },
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    cardElement.mount('#update-card-element');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    stripeCardElement.value = {stripe, cardElement};
  });
};

const initializeStripe = (): void => {
  if (!setupIntentResponse.value) {
    return;
  }

  if (window.Stripe) {
    stripeLoaded.value = true;
    setupStripeElements();
  } else {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = (): void => {
      stripeLoaded.value = true;
      setupStripeElements();
    };
    script.onerror = (): void => {
      // The shown copy is the generic one, so keep the actual failure mode in the log.
      logger.error('Failed to load Stripe.js from', script.src)();
      error.value = t('update_error_generic');
      updateState.value = UpdateState.ERROR;
    };
    document.head.appendChild(script);
  }
};

const createSetupIntent = async(): Promise<void> => {
  updateState.value = UpdateState.LOADING;
  error.value = null;

  try {
    const response = await api.me.createPaymentMethodSetupIntent();
    setupIntentResponse.value = response;
    initializeStripe();
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : null;
    error.value = errorMessage;
    updateState.value = UpdateState.ERROR;
    if (errorMessage) {
      emit('updateError', errorMessage);
    }
  }
};

const processUpdate = async(): Promise<void> => {
  if (!stripeCardElement.value || !setupIntentResponse.value) {
    return;
  }

  isProcessing.value = true;

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const {stripe, cardElement} = stripeCardElement.value;

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
       @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const {setupIntent, error: confirmError} = await stripe.confirmCardSetup(setupIntentResponse.value.clientSecret, {
      // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unsafe-assignment
      payment_method: {card: cardElement},
    });

    if (confirmError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      error.value = confirmError.message || null;
      updateState.value = UpdateState.ERROR;
      isProcessing.value = false;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (confirmError.message) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        emit('updateError', confirmError.message);
      }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (setupIntent?.payment_method) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      await api.me.updatePaymentMethod(setupIntent.payment_method);
      isProcessing.value = false;
      updateState.value = UpdateState.SUCCESS;
      emit('updateSuccess');
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : null;
    error.value = errorMessage;
    updateState.value = UpdateState.ERROR;
    isProcessing.value = false;
    if (errorMessage) {
      emit('updateError', errorMessage);
    }
  }
};

const handleDialogOpen = (): void => {
  if (props.modelValue) {
    // Always reset state and create fresh setup intent when opening dialog
    updateState.value = UpdateState.LOADING;
    error.value = null;
    setupIntentResponse.value = null;

    void createSetupIntent();
  }
};

const retryUpdate = (): void => {
  void createSetupIntent();
};

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    handleDialogOpen();
  }
}, {immediate: true});
</script>

<template>
  <balm-dialog
    v-model="dialogValue"
    type="fit-content"
    theme="grey-10"
    :no-x="isProcessing"
    @update:model-value="closeDialog"
  >
    <div class="update-card-dialog">
      <!-- Processing Overlay -->
      <div
        v-if="isProcessing"
        class="processing-overlay"
      >
        <div class="loading-content">
          <div class="form-icon">
            <svg-illustration name="payment-sun.svg"/>
          </div>
          <h3 class="loading-title">
            {{ $t('updating_card') }}
          </h3>
          <div class="loading-icon">
            <loader-animation/>
          </div>
          <p class="loading-description">
            {{ $t('please_wait_processing') }}
          </p>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="updateState === UpdateState.LOADING"
        class="loading-overlay"
      >
        <div class="loading-content">
          <div class="form-icon">
            <svg-illustration name="payment-sun.svg"/>
          </div>
          <h3 class="loading-title">
            {{ $t('setting_up_update') }}
          </h3>
          <div class="loading-icon">
            <loader-animation/>
          </div>
          <p class="loading-description">
            {{ $t('preparing_secure_update') }}
          </p>
        </div>
      </div>

      <!-- Update Form State -->
      <div
        v-else-if="updateState === UpdateState.FORM"
        class="update-state form-state"
      >
        <div class="form-header">
          <div class="form-icon">
            <svg-illustration name="payment-sun.svg"/>
          </div>
          <h2 class="form-title">
            {{ $t('update_payment_method') }}
          </h2>
          <p class="form-description">
            {{ $t('update_card_description') }}
          </p>
        </div>

        <div class="update-form">
          <div class="card-section">
            <label class="card-label">{{ $t('card_details') }}</label>
            <div
              id="update-card-element"
              class="card-element"
            />
          </div>

          <div class="form-actions">
            <balm-btn
              :label="$t('update_card')"
              theme="blue"
              class="update-button"
              :loading="isProcessing"
              :disable="isProcessing"
              @click="processUpdate"
            />
            <balm-btn
              :label="$t('cancel')"
              theme="grey"
              class="cancel-button"
              :disable="isProcessing"
              @click="closeDialog"
            />
          </div>
        </div>
      </div>

      <!-- Success State -->
      <div
        v-else-if="updateState === UpdateState.SUCCESS"
        class="update-state success-state"
      >
        <div class="form-icon">
          <svg-illustration name="payment-sun.svg"/>
        </div>
        <h2 class="success-title">
          {{ $t('card_updated') }}
        </h2>
        <div class="success-illustration">
          <svg-illustration name="payment-thumbs-up.svg"/>
        </div>
        <p class="success-description">
          {{ $t('card_updated_description') }}
        </p>
        <balm-btn
          :label="$t('done')"
          theme="blue"
          class="done-button"
          @click="closeDialog"
        />
      </div>

      <!-- Error State -->
      <div
        v-else-if="updateState === UpdateState.ERROR"
        class="update-state error-state"
      >
        <div class="state-icon error-icon">
          <svg-illustration name="x-close.svg"/>
        </div>
        <h2 class="state-title error-title">
          {{ $t('update_failed') }}
        </h2>
        <p class="state-description error-description">
          {{ error || $t('update_error_generic') }}
        </p>
        <div class="error-actions">
          <balm-btn
            :label="$t('try_again')"
            theme="blue"
            class="full-width"
            @click="retryUpdate"
          />
          <balm-btn
            :label="$t('cancel')"
            theme="grey"
            class="full-width"
            @click="closeDialog"
          />
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.update-card-dialog {
  position: relative;
  overflow: hidden;
  padding: var(--Spacing-spacing-4);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @include screen-md {
    min-width: 460px;
  }
}

.update-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 240px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.4s ease-out;
}

.state-icon {
  margin-bottom: var(--Spacing-spacing-5);
  display: flex;
  align-items: center;
  justify-content: center;

  &.error-icon {
    width: 80px;
    height: 80px;
    background: var(--Primary-primary-color-3);
    border-radius: 50%;
    box-shadow: 0 8px 24px rgba(203, 109, 82, 0.2);
    animation: errorShake 0.5s ease-out;

    :deep(svg) {
      width: 40px;
      height: 40px;
      fill: white;
    }
  }
}

.state-title {
  @include font-h2;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-100);
  font-weight: 600;

  &.error-title {
    color: var(--Primary-primary-color-3);
  }
}

.state-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 360px;
  line-height: 1.6;

  &.error-description {
    color: var(--Primary-primary-color-3);
    background: rgba(203, 109, 82, 0.05);
    padding: var(--Spacing-spacing-3);
    border-radius: var(--Spacing-spacing-2);
    border: 1px solid rgba(203, 109, 82, 0.1);
  }
}

.form-state {
  align-items: center;
  text-align: center;
  position: relative;
}

.form-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: var(--Spacing-spacing-5);
  padding-bottom: var(--Spacing-spacing-4);
  border-bottom: 2px solid var(--Neutral-UI-neutral-ui-10);
}

.form-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--Spacing-spacing-4);
}

.form-title {
  @include font-display-3;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-100);
}

.form-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  max-width: 300px;
}

.update-form {
  width: 100%;
}

.card-section {
  margin-bottom: var(--Spacing-spacing-5);
  text-align: left;
  width: 100%;
}

.card-label {
  @include font-body-sm;
  display: block;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-90);
  line-height: 100%;
}

.card-element {
  padding: var(--Spacing-spacing-4);
  border: 2px solid var(--Neutral-UI-neutral-ui-15);
  border-radius: var(--Spacing-spacing-3);
  background: var(--Neutral-UI-neutral-ui-0);
  min-height: 56px;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--Primary-primary-color-1);
    box-shadow: 0 4px 12px rgba(93, 131, 178, 0.08);
  }

  &:focus-within {
    border-color: var(--Primary-primary-color-1);
    box-shadow: 0 0 0 4px rgba(93, 131, 178, 0.12);
  }
}

.form-actions,
.error-actions {
  display: flex;
  gap: var(--Spacing-spacing-3);
  flex-direction: column;
  width: 100%;
}

.update-button,
.cancel-button,
.done-button,
.full-width {
  width: 100%;
}

.success-state {
  min-height: 200px;
  justify-content: center;
  padding: var(--Spacing-spacing-4);
}

.success-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-4);
}

.success-illustration {
  margin-bottom: var(--Spacing-spacing-4);

  :deep(svg) {
    width: 160px;
    height: auto;
  }
}

.success-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 300px;
  text-align: center;
}

.error-state {
  min-height: 200px;
  justify-content: center;
  width: 100%;
}

.loading-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-3);
}

.loading-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
}

.loading-icon {
  transform: scale(0.5);
  margin: calc(-1 * var(--Spacing-spacing-4)) 0;
}

.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 400px;
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--Neutral-UI-neutral-ui-10);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: var(--Border-Radius-1);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 var(--Spacing-spacing-4);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes errorShake {
  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}
</style>
